/**
 * Binom catalog read hooks for the Campaign tab dropdowns + pickers:
 * traffic-sources, groups (by subject), domains, tags, affiliate-networks,
 * countries, languages.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery } from '@tanstack/react-query';

import api from '@/services/api';
import { binomKeys } from './useBinom';

export interface BinomTrafficSource {
  id: string;
  name: string;
}
export interface BinomGroup {
  uuid: string;
  name: string;
  type?: string;
}
export interface BinomDomain {
  uuid: string;
  host: string;
}
export interface BinomTag {
  id: string;
  name: string;
}
export interface BinomAffiliateNetwork {
  id: string;
  name: string;
}
export interface BinomCountry {
  code: string;
  name: string;
}
export interface BinomLanguage {
  code: string;
  name: string;
}

const catalogKey = (kind: string) => [...binomKeys.all, 'catalog', kind] as const;

function asArray<T>(v: unknown): T[] {
  if (Array.isArray(v)) return v as T[];
  if (
    v &&
    typeof v === 'object' &&
    Array.isArray((v as { items?: unknown }).items)
  ) {
    return (v as { items: T[] }).items;
  }
  return [];
}

export function useBinomTrafficSources() {
  return useQuery({
    queryKey: catalogKey('traffic-sources'),
    queryFn: async () => {
      const r = await api.get<{ data: BinomTrafficSource[] | unknown }>(
        '/binom/traffic-sources',
      );
      return asArray<BinomTrafficSource>(r.data?.data);
    },
    staleTime: 60_000,
  });
}

export type BinomGroupSubject =
  | 'campaigns'
  | 'offers'
  | 'landings'
  | 'trafficSources'
  | 'domains'
  | 'rotations';

export function useBinomGroups(subject: BinomGroupSubject = 'campaigns') {
  return useQuery({
    queryKey: [...catalogKey('groups'), subject] as const,
    queryFn: async () => {
      const r = await api.get<{ data: BinomGroup[] | unknown }>(
        `/binom/groups?subject=${encodeURIComponent(subject)}`,
      );
      return asArray<BinomGroup>(r.data?.data);
    },
    staleTime: 60_000,
  });
}

export function useBinomDomains() {
  return useQuery({
    queryKey: catalogKey('domains'),
    queryFn: async () => {
      const r = await api.get<{ data: BinomDomain[] | unknown }>('/binom/domains');
      return asArray<BinomDomain>(r.data?.data);
    },
    staleTime: 60_000,
  });
}

export function useBinomTags() {
  return useQuery({
    queryKey: catalogKey('tags'),
    queryFn: async () => {
      const r = await api.get<{ data: BinomTag[] | unknown }>('/binom/tags');
      return asArray<BinomTag>(r.data?.data);
    },
    staleTime: 60_000,
  });
}

export function useBinomAffiliateNetworks() {
  return useQuery({
    queryKey: catalogKey('affiliate-networks'),
    queryFn: async () => {
      const r = await api.get<{ data: BinomAffiliateNetwork[] | unknown }>(
        '/binom/affiliate-networks',
      );
      return asArray<BinomAffiliateNetwork>(r.data?.data);
    },
    staleTime: 60_000,
  });
}

export function useBinomCountries() {
  return useQuery({
    queryKey: catalogKey('countries'),
    queryFn: async () => {
      const r = await api.get<{ data: BinomCountry[] | unknown }>(
        '/binom/countries',
      );
      return asArray<BinomCountry>(r.data?.data);
    },
    staleTime: 60 * 60_000,
  });
}

export function useBinomLanguages() {
  return useQuery({
    queryKey: catalogKey('languages'),
    queryFn: async () => {
      const r = await api.get<{ data: BinomLanguage[] | unknown }>(
        '/binom/languages',
      );
      return asArray<BinomLanguage>(r.data?.data);
    },
    staleTime: 60 * 60_000,
  });
}
